import { lazy } from 'react'
import type { ComponentType } from 'react'

const STALE_CHUNK_RELOAD_KEY = 'beoflow_stale_chunk_reload'

/**
 * Marker message for the BEO-3208 failure: Vite's preload helper resolves
 * `undefined` instead of rejecting when a `vite:preloadError` listener calls
 * preventDefault() — which main.tsx does (BEO-2381) — so a failed chunk load
 * reaches lazyWithReload as a SUCCESSFUL resolution without a module. Left
 * alone, React's lazyInitializer stores it as a resolved payload and crashes
 * reading `payload._result.default`. Treated as a chunk-load failure so the
 * retry / reload-once recovery below runs.
 */
export const MISSING_MODULE_MESSAGE =
  'Dynamically imported module resolved without a value (chunk preload failed)'

/**
 * True when an error is a dynamic-import / chunk-load failure — the signature of
 * a stale chunk after a deploy. Covers the variants observed across browsers in
 * Sentry (BEO-2381): Vite/Chromium ("Failed to fetch dynamically imported
 * module"), Firefox ("error loading dynamically imported module"), and Safari
 * ("Importing a module script failed", MIME-type / "Load failed" when the SPA
 * fallback index.html is served in place of the missing JS chunk).
 */
export function isStaleChunkError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : String(reason)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('not a valid JavaScript MIME type') ||
    message.includes('Load failed') ||
    message.includes('Unable to preload') ||
    message.includes(MISSING_MODULE_MESSAGE)
  )
}

// Set when this page life dispatches its stale-chunk reload; see isStaleChunkReloadInFlight.
let reloadDispatched = false

/**
 * Force a single hard reload per release to recover from a stale chunk after a
 * deploy. Returns true if a reload was triggered; false if a reload was already
 * performed for this release (the caller should then let the error surface
 * rather than loop forever on a genuinely broken build).
 *
 * The guard is keyed by release so a deploy that fixes the build clears the
 * one-reload budget for the next stale chunk the same tab might hit.
 */
export function reloadOnceForStaleChunk(): boolean {
  const release = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ?? 'dev'
  const key = `${STALE_CHUNK_RELOAD_KEY}:${release}`
  try {
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
  } catch {
    // sessionStorage unavailable (private mode / disabled). Best effort: reload
    // anyway — a stale chunk after deploy is resolved by the first reload in
    // practice, and the worst case without the guard is a single extra reload.
  }
  reloadDispatched = true
  window.location.reload()
  return true
}

/**
 * True once this page life has dispatched its stale-chunk reload. Distinct from
 * reloadOnceForStaleChunk() returning false, which also means "budget spent by a
 * PREVIOUS page life" (a genuinely broken build). main.tsx's vite:preloadError
 * listener usually spends the budget before lazyWithReload or ErrorBoundary see
 * the failure (BEO-3208), so they check this to keep waiting for that reload
 * instead of surfacing the crash UI and a Sentry event.
 */
export function isStaleChunkReloadInFlight(): boolean {
  return reloadDispatched
}

/**
 * Drop-in replacement for React.lazy that survives stale-chunk-after-deploy.
 *
 * On a Vite + lazy() SPA, each Cloudflare deploy rotates hashed chunk filenames.
 * A tab still holding the previous index.html requests an old chunk path that no
 * longer exists; Cloudflare serves the SPA fallback index.html (text/html), the
 * dynamic import rejects, and React's lazyInitializer crashes reading
 * `_result.default` (BEO-2381). We intercept at the import-promise level so the
 * rejection never reaches lazyInitializer:
 *   1. Retry the import once — covers transient network blips / preload races.
 *   2. If it still fails with a chunk-load error, hard-reload once per release so
 *      the browser fetches fresh HTML with current chunk hashes.
 *   3. Re-throw anything else (and chunk errors after the reload budget is spent)
 *      so React's Suspense/ErrorBoundary handle real failures normally.
 */
async function load<P>(factory: () => Promise<{ default: ComponentType<P> }>) {
  const resolved = await factory()
  // BEO-3208: when main.tsx's vite:preloadError listener preventDefaults the
  // error, Vite's preload helper resolves undefined instead of rejecting. Treat
  // that as a chunk-load failure so the recovery path runs instead of feeding
  // React an empty module (lazyInitializer crashes reading `.default`).
  if (resolved == null) throw new Error(MISSING_MODULE_MESSAGE)
  return resolved
}

export function lazyWithReload<P>(factory: () => Promise<{ default: ComponentType<P> }>) {
  return lazy(async () => {
    try {
      return await load(factory)
    } catch (error) {
      if (!isStaleChunkError(error)) throw error
      try {
        // One retry first — a transient failure resolves without a reload.
        return await load(factory)
      } catch (retryError) {
        if (isStaleChunkError(retryError) && (reloadOnceForStaleChunk() || isStaleChunkReloadInFlight())) {
          // Reload is in flight. Return a never-resolving promise so React keeps
          // showing the Suspense fallback until navigation tears the page down.
          return new Promise<{ default: ComponentType<P> }>(() => {})
        }
        throw retryError
      }
    }
  })
}
